import { CareOSLoader } from '@/components/ui/careos-loader';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  ChevronRight,
  Loader2,
  AlertTriangle,
  Info,
  AlertCircle,
  CreditCard
} from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export default function NotificationsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notifications, unreadCount, isLoading, markAsRead, markAllAsRead, refetch } = useNotifications();
  const [isDeletingAll, setIsDeletingAll] = useState(false);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'alert':
      case 'stock_alert':
      case 'low_stock':
        return <AlertTriangle className="w-5 h-5" />;
      case 'critical':
      case 'vital_alert':
        return <AlertCircle className="w-5 h-5" />;
      case 'task_execution':
      case 'task_registered':
        return <CheckCheck className="w-5 h-5" />;
      case 'task_comment':
      case 'comment_reply':
      case 'mention':
        return <Bell className="w-5 h-5" />; // Message/Bell icon for social
      case 'incident':
        return <AlertTriangle className="w-5 h-5 text-destructive" />;
      case 'billing_payment_failed':
      case 'billing_grace_expired':
        return <CreditCard className="w-5 h-5" />;
      case 'billing_recovered':
        return <CreditCard className="w-5 h-5" />;
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'alert':
      case 'stock_alert':
      case 'low_stock':
      case 'billing_payment_failed':
        return 'text-warning bg-warning/10';
      case 'critical':
      case 'billing_grace_expired':
        return 'text-destructive bg-destructive/10';
      case 'billing_recovered':
        return 'text-success bg-success/10';
      default:
        return 'text-primary bg-primary/10';
    }
  };

  const handleDeleteNotification = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', id);

      if (error) throw error;

      refetch();
      toast.success('Notificação removida');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Erro ao remover notificação');
    }
  };

  const handleDeleteAll = async () => {
    if (!user) return;
    setIsDeletingAll(true);
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;

      refetch();
      toast.success('Todas as notificações foram removidas');
    } catch (error) {
      console.error('Error deleting all notifications:', error);
      toast.error('Erro ao remover notificações');
    } finally {
      setIsDeletingAll(false);
    }
  };

  const handleMarkAsRead = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await markAsRead(id);
  };

  return (
    <AppLayout title={t('notifications.title')} showBack onBack={() => navigate(-1)} showFAB={false}>
      <div className="p-4 space-y-4 pb-24">
        {/* Action Buttons */}
        {notifications.length > 0 && (
          <div className="flex gap-2">
            {unreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={markAllAsRead}
                className="flex-1"
              >
                <CheckCheck className="w-4 h-4 mr-2" />
                {t('notifications.markAllAsRead')}
              </Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-destructive hover:text-destructive"
                  disabled={isDeletingAll}
                >
                  {isDeletingAll ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  {t('notifications.removeAll')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('notifications.removeConfirmTitle')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('notifications.removeConfirmDesc')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteAll}>
                    {t('notifications.removeAll')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}

        {/* Notifications List */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <CareOSLoader size="lg" label={t('common.loading')} />
          </div>
        ) : notifications.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{t('notifications.empty')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <Card
                key={notification.id}
                className={cn(
                  "cursor-pointer hover:bg-accent/50 transition-colors",
                  !notification.is_read && "border-primary/50 bg-primary/5"
                )}
                onClick={() => navigate(`/app/notifications/${notification.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      "p-2 rounded-full shrink-0",
                      getNotificationColor(notification.type)
                    )}>
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-sm line-clamp-1">
                          {notification.title}
                        </p>
                        {!notification.is_read && (
                          <Badge variant="default" className="text-[12px] px-1.5 py-0">
                            Nova
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {format(new Date(notification.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!notification.is_read && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => handleMarkAsRead(notification.id, e)}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={(e) => handleDeleteNotification(notification.id, e)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
